import React, { useCallback, useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { MaskHappy, CaretDown, TestTube } from "@phosphor-icons/react";
import { toast } from "sonner";
import api from "@/lib/api";

/**
 * Iter-71 — Multi-Role Impersonate widget.
 *
 * Super_admin header shortcut that lists the 4 seeded Iter-70 test
 * accounts (`test-employee`, `test-mediabuyer`, `test-buyer`,
 * `test-vendor`) and one-click impersonates each. Fastest possible
 * role-switching for demos.
 *
 * Fetches candidates via `GET /employees?q=test-`. Hidden when:
 *   - actor isn't super_admin, or
 *   - actor is already impersonating (nested impersonation is
 *     refused server-side anyway; hiding avoids a confusing button).
 *
 * If zero test accounts are found the dropdown surfaces a
 * "Seed test accounts" call-to-action that deep-links into
 * `/employees` where the Iter-70 button lives.
 */
const ROLE_LABEL = {
  employee: "Employee",
  media_buyer: "Media Buyer",
  buyer: "Buyer",
  vendor: "Affiliate",
};

// Preferred order — mirrors the columns on the /employees Seed
// receipt table so ops see the same layout everywhere.
const ROLE_ORDER = ["employee", "media_buyer", "buyer", "vendor"];

export default function MultiRoleImpersonateWidget({ user, refresh }) {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null);

  const visible = user?.role === "super_admin" && !user?.impersonated_by;

  const load = useCallback(async () => {
    if (!visible) return;
    setLoading(true);
    try {
      const { data } = await api.get("/employees?q=test-");
      // Keep only rows flagged is_test_account=true so the widget can't
      // accidentally impersonate a real user whose email happens to
      // include "test-".
      const list = (data.employees || []).filter((u) => u.is_test_account);
      list.sort((a, b) =>
        ROLE_ORDER.indexOf(a.role) - ROLE_ORDER.indexOf(b.role));
      setRows(list);
    } catch {
      setRows([]);
    } finally { setLoading(false); }
  }, [visible]);

  useEffect(() => { if (open) load(); }, [open, load]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (!e.target.closest("[data-testid='multi-impersonate-root']")) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const impersonate = async (row) => {
    setBusy(row.id);
    try {
      await api.post(`/auth/impersonate/${row.id}`);
      toast.success(`Now viewing as ${row.email}`);
      await refresh?.();
      nav("/", { replace: true });
      setOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Impersonate failed");
    } finally { setBusy(null); }
  };

  if (!visible) return null;

  return (
    <div className="relative" data-testid="multi-impersonate-root">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid="multi-impersonate-trigger"
        className="flex items-center gap-2 px-3 py-1.5 border border-black bg-amber-200 hover:bg-amber-300 transition-colors duration-100 text-xs uppercase tracking-widest whitespace-nowrap"
        title="Impersonate a seeded test account"
      >
        <MaskHappy size={12} weight="bold" /> Test as
        <CaretDown size={10} weight="bold" />
      </button>
      {open && (
        <div
          className="absolute right-0 top-full mt-1 w-72 border-2 border-black bg-white brut-shadow-sm z-50"
          data-testid="multi-impersonate-menu"
        >
          <div className="px-3 py-2 border-b border-zinc-300 label-mono flex items-center gap-1">
            <TestTube size={11} weight="bold" /> Log in as any role
          </div>
          {loading && (
            <div className="px-3 py-3 text-[11px] text-zinc-500">Loading…</div>
          )}
          {!loading && rows.length === 0 && (
            <div className="p-3 text-[11px] text-zinc-700 leading-relaxed" data-testid="multi-impersonate-empty">
              No seeded test accounts found.
              <NavLink
                to="/employees"
                onClick={() => setOpen(false)}
                data-testid="multi-impersonate-seed-cta"
                className="mt-2 block text-center border border-black px-2 py-1 uppercase text-[10px] tracking-widest hover:bg-black hover:text-white"
              >
                Go seed 4 test accounts
              </NavLink>
            </div>
          )}
          {!loading && rows.map((r) => (
            <button
              key={r.id}
              type="button"
              onClick={() => impersonate(r)}
              disabled={busy === r.id}
              data-testid={`multi-impersonate-row-${r.role}`}
              className="w-full text-left px-3 py-2 border-b border-zinc-100 hover:bg-zinc-100 flex items-start gap-2 disabled:opacity-50"
            >
              <MaskHappy size={12} weight="bold" className="mt-0.5" />
              <span className="flex-1 min-w-0">
                <span className="block font-bold text-xs">
                  {ROLE_LABEL[r.role] || r.role}
                </span>
                <span className="block font-mono text-[10px] text-zinc-500 truncate">
                  {r.email}
                </span>
              </span>
              {busy === r.id && (
                <span className="text-[9px] uppercase tracking-widest text-zinc-500">…</span>
              )}
            </button>
          ))}
          {!loading && rows.length > 0 && (
            <div className="px-3 py-2 border-t border-zinc-300 text-[9px] uppercase tracking-widest text-zinc-500 leading-relaxed">
              Every action while impersonating is audit-logged.
            </div>
          )}
        </div>
      )}
    </div>
  );
}
